import {
  adjustedPlatformTotal,
  formatDelta,
  getBankrollBaseValue,
  getHistoryAdjustedTotal,
  localDateKey,
} from "../../services/simplifiedBankroll";
import { comparePlatformNames } from "./simplifiedAccounts";

export const getSimplifiedChartPlatformOptions = (accounts = []) =>
  [
    ...new Set(
      accounts
        .map((account) => String(account.name || "").trim())
        .filter(Boolean)
    ),
  ].sort(comparePlatformNames);

const getOrderedDateRange = (startDate, endDate) => {
  if (!startDate || !endDate || startDate <= endDate) {
    return { startDate, endDate };
  }

  return { startDate: endDate, endDate: startDate };
};

const isEntryInsideDateRange = (entry, filters = {}) => {
  const date = String(entry?.date || "");
  const { startDate, endDate } = getOrderedDateRange(
    filters.startDate,
    filters.endDate
  );

  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
};

const filterObjectByPlatforms = (values = {}, selectedPlatforms) =>
  Object.fromEntries(
    Object.entries(values).filter(([platformName]) =>
      selectedPlatforms.has(platformName)
    )
  );

const normalizeSelectedPlatforms = (filters = {}, platformOptions = []) => {
  const requestedPlatforms = Array.isArray(filters.platforms)
    ? filters.platforms
    : [filters.platform];
  const availablePlatforms = new Set(platformOptions);

  return [
    ...new Set(
      requestedPlatforms
        .map((platform) => String(platform || "").trim())
        .filter((platform) => platform && availablePlatforms.has(platform))
    ),
  ].sort(comparePlatformNames);
};

export const filterHistoryEntryByPlatform = (entry, platform) => {
  if (!platform) return entry;

  return filterHistoryEntryByPlatforms(entry, [platform]);
};

export const filterHistoryEntryByPlatforms = (entry, platforms = []) => {
  const selectedPlatforms = new Set(platforms);
  if (!selectedPlatforms.size) return entry;

  const accounts = Array.isArray(entry.accounts)
    ? entry.accounts.filter((account) => selectedPlatforms.has(account.name))
    : [];

  return {
    ...entry,
    accounts,
    platforms: filterObjectByPlatforms(entry.platforms, selectedPlatforms),
    withdrawals: filterObjectByPlatforms(entry.withdrawals, selectedPlatforms),
    deposits: filterObjectByPlatforms(entry.deposits, selectedPlatforms),
  };
};

const hasPlatformData = (entry, platforms = []) => {
  if (!platforms.length) return true;
  if (Array.isArray(entry.accounts) && entry.accounts.length) return true;
  return Boolean(Object.keys(entry.platforms || {}).length);
};

const formatChartDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });

export const buildSimplifiedChartModel = ({
  bankroll,
  accounts,
  hasAccountValues,
  filters = {},
  chartColors,
  displayCurrency,
}) => {
  const history = Array.isArray(bankroll.history) ? bankroll.history : [];
  const historyWithCurrent =
    hasAccountValues && !history.some((entry) => entry.date === localDateKey())
      ? [
          ...history,
          {
            date: localDateKey(),
            base: bankroll.base,
            baseCurrency: bankroll.baseCurrency,
            accounts,
          },
        ]
      : history;
  const platformOptions = getSimplifiedChartPlatformOptions(accounts);
  const selectedPlatforms = normalizeSelectedPlatforms(filters, platformOptions);
  const sortedHistory = historyWithCurrent
    .filter((entry) => isEntryInsideDateRange(entry, filters))
    .map((entry) => filterHistoryEntryByPlatforms(entry, selectedPlatforms))
    .filter((entry) => hasPlatformData(entry, selectedPlatforms))
    .sort((a, b) => a.date.localeCompare(b.date));
  const currencyOptions = { displayCurrency };
  const historyTotals = sortedHistory.map((entry) =>
    selectedPlatforms.length
      ? adjustedPlatformTotal(entry, currencyOptions)
      : getHistoryAdjustedTotal(entry, currencyOptions)
  );
  const chartBaseline = selectedPlatforms.length
    ? historyTotals[0] || 0
    : getBankrollBaseValue(bankroll, currencyOptions);
  const chartValues = historyTotals.map((value, index) =>
    selectedPlatforms.length
      ? value - chartBaseline
      : value - getBankrollBaseValue(sortedHistory[index], currencyOptions)
  );
  const pointLabels = chartValues.map((value, index) =>
    index === 0
      ? formatDelta(0, displayCurrency)
      : formatDelta(value - chartValues[index - 1], displayCurrency)
  );
  const chartFinal = chartValues[chartValues.length - 1] || 0;
  const lucroDia =
    chartValues.length > 1
      ? chartValues[chartValues.length - 1] - chartValues[chartValues.length - 2]
      : 0;
  const selectedPlatformLabel =
    selectedPlatforms.length === 1
      ? selectedPlatforms[0]
      : selectedPlatforms.length
        ? "Plataformas selecionadas"
        : "Resultado";
  const chartData = {
    labels: sortedHistory.map((entry) => formatChartDate(entry.date)),
    datasets: [
      {
        label: selectedPlatformLabel,
        data: chartValues,
        currency: displayCurrency,
        pointLabels,
        borderColor: chartFinal >= 0 ? chartColors.positive : chartColors.danger,
        backgroundColor:
          chartFinal >= 0
            ? chartColors.positiveFill || "rgba(76,175,130,0.1)"
            : chartColors.dangerFill || "rgba(231,76,60,0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: chartColors.accent,
        pointBorderColor: chartColors.panel,
        pointBorderWidth: 2,
      },
    ],
  };

  return {
    chartData,
    chartFinal,
    chartValues,
    hasSourceHistory: historyWithCurrent.length > 0,
    lucroDia,
    platformOptions,
    pointLabels,
    selectedPlatform: selectedPlatforms.length === 1 ? selectedPlatforms[0] : "",
    selectedPlatformLabel,
    selectedPlatforms,
    sortedHistory,
  };
};
